/** Enrich (v1 market-backfill.js recheckMarket, verbatim contract) — pulls
 * Zestimate, tax, price history, county appraiser record, flood zone and
 * permit links for ONE lead, on demand:
 *   POST /api/inbox/market-check {address,id,org_id,zillow_url} → status +
 *        data{...} + enriched[] + stamped (server wrote the properties row)
 *   POST /api/inbox/county-check {address,county,parcel,id,org_id} → county
 *        living/gross sqft, owner, annual tax, subdivision, legal description
 *   POST /api/inbox/flood-check {address,id,org_id} → FEMA zone + SFHA flag
 * The row-level fields (zestimate, beds, price/tax history, county links) are
 * STAMPED server-side — the caller refetches leads when `stamped` comes back.
 * Everything else lands as LEAD_EDITS (v1 _applyEnrichData semantics: fill
 * only fields the user hasn't typed; permitUrl/countyAppraiser overwrite).
 * County + flood are best-effort — a failure never kills the run (v1 parity). */
import { backendFetch } from "../../lib/backend";
import { moneyExact } from "../../lib/money";

export interface EnrichOutcome {
  /** lead_fields edits to write (already empty-checked against current edits) */
  edits: Record<string, string>;
  marketStatus: "on" | "off" | null;
  /** the server wrote the properties row — refetch to show zest/tax/history */
  stamped: boolean;
  /** server-side stamped field count (v1 j.enriched.length) */
  enrichedCount: number;
  floodZone: string | null;
  /** e.g. "no_scrapfly_key" — surfaces v1's exact fallback message */
  detail: string | null;
}

/** v1 home_type → label map, verbatim. */
const HOME_TYPE_LABEL: Record<string, string> = {
  SINGLE_FAMILY: "Single Family", TOWNHOUSE: "Townhouse", CONDO: "Condo",
  MULTI_FAMILY: "Multi-Family", MANUFACTURED: "Mobile / Manufactured",
  LOT: "Vacant Land", LAND: "Vacant Land", APARTMENT: "Multi-Family",
};

const sv = (v: unknown): string => (v === null || v === undefined ? "" : String(v).trim());
/** v1 _fmtUSD on the money-ish fields (assessed / last sold). */
const usd = (v: unknown): string => {
  const n = typeof v === "number" ? v : Number(String(v).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) && n !== 0 ? moneyExact(n) : sv(v);
};

const isObj = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/** v1 _applyEnrichData, the LEAD_EDITS half: fill empty fields only, except
 * permitUrl / countyAppraiser which always follow the freshest link. */
export function enrichEditsFromMarket(
  data: unknown,
  fields: Record<string, unknown> | undefined,
): Record<string, string> {
  if (!isObj(data)) return {};
  const d = data;
  const out: Record<string, string> = {};
  const cur = (k: string): string => {
    if (out[k]) return out[k] as string;
    const v = fields?.[k];
    return typeof v === "string" && v.trim() !== "" ? v : "";
  };
  const fill = (k: string, v: unknown, money = false) => {
    const s = money ? usd(v) : sv(v);
    if (!s || sv(v) === "") return;
    if (cur(k)) return;
    out[k] = s;
  };
  fill("yearBuilt", d["year_built"]);
  fill("parcel", d["parcel_number"]);
  fill("assessed", d["assessed_value"], true);
  fill("countyTax", d["county_annual_tax"]);
  fill("lastSold", d["last_sold_price"], true);
  fill("lotSize", d["lot_size"]);
  if (d["hoa_fee"] !== null && d["hoa_fee"] !== undefined && d["hoa_fee"] !== "") fill("hoa", d["hoa_fee"]);
  fill("zillowUrl", d["property_url"]);
  if (sv(d["home_type"])) {
    const key = sv(d["home_type"]).toUpperCase();
    const label = HOME_TYPE_LABEL[key] ??
      sv(d["home_type"]).replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    fill("ptype", label);
  }
  const pu = sv(d["permit_search_url"]);
  if (pu && cur("permitUrl") !== pu) out["permitUrl"] = pu;
  const ca = sv(d["county_appraiser"]);
  if (ca && cur("countyAppraiser") !== ca) out["countyAppraiser"] = ca;
  return out;
}

/** v1's county-check apply (the _cs calls): fill-empty-only, no overwrites. */
export function enrichEditsFromCounty(
  data: unknown,
  fields: Record<string, unknown> | undefined,
  already: Record<string, string>,
): Record<string, string> {
  if (!isObj(data)) return {};
  const d = data;
  const out: Record<string, string> = {};
  const cur = (k: string): string => {
    if (already[k] || out[k]) return "x";
    const v = fields?.[k];
    return typeof v === "string" && v.trim() !== "" ? v : "";
  };
  const fill = (k: string, v: unknown) => {
    const s = sv(v);
    if (!s || cur(k)) return;
    out[k] = s;
  };
  fill("countySqft", d["county_living_sqft"]);
  fill("totalSqft", d["county_gross_area"]);
  fill("owner", d["county_owner"]);
  fill("countyTax", d["county_annual_tax"]);
  fill("subdivision", d["county_subdivision"]);
  fill("legalDesc", d["county_legal_description"]);
  return out;
}

export async function runEnrichLead(
  token: string | null,
  args: {
    address: string;
    /** properties row id (null for manual leads — the server just won't stamp) */
    id: string | null;
    orgId: string;
    county: string | null;
    fields: Record<string, unknown> | undefined;
  },
  opts?: { onWake?: () => void; onStep?: (msg: string) => void; signal?: AbortSignal },
): Promise<EnrichOutcome> {
  const step = opts?.onStep ?? (() => {});
  const common = { ...(opts?.onWake ? { onWake: opts.onWake } : {}), ...(opts?.signal ? { signal: opts.signal } : {}) };
  const zillowUrl = typeof args.fields?.["zillowUrl"] === "string" ? (args.fields["zillowUrl"] as string) : "";

  // 1. Zillow market check — the main pull; a failure here fails the run.
  step("Enriching from Zillow / county…");
  const j = await backendFetch<Record<string, unknown>>(token, "/api/inbox/market-check", {
    method: "POST",
    body: { address: args.address, id: args.id, org_id: args.orgId, zillow_url: zillowUrl },
    ...common,
  });
  const status = sv(j?.["status"]);
  const marketStatus: "on" | "off" | null =
    status && status !== "unknown" ? (status === "on_market" ? "on" : "off") : null;
  const data = isObj(j?.["data"]) ? (j["data"] as Record<string, unknown>) : {};
  const edits = enrichEditsFromMarket(data, args.fields);
  let stamped = j?.["stamped"] === true;
  const enrichedCount = Array.isArray(j?.["enriched"]) ? (j["enriched"] as unknown[]).length : 0;
  const detail = sv(j?.["detail"]) || null;

  // 2. County appraiser record — best-effort (unsupported counties no-op).
  const county = sv(data["county"]) || sv(args.county);
  if (county) {
    step("Pulling the county appraiser record…");
    try {
      const cj = await backendFetch<Record<string, unknown>>(token, "/api/inbox/county-check", {
        method: "POST",
        body: {
          address: args.address, county,
          parcel: sv(data["parcel_number"]) || sv(args.fields?.["parcel"]),
          id: args.id, org_id: args.orgId,
        },
        ...common,
      });
      Object.assign(edits, enrichEditsFromCounty(cj?.["data"], args.fields, edits));
    } catch { /* v1: county lookup is best-effort */ }
  }

  // 3. Flood zone — best-effort; the zone ALWAYS overwrites (v1 semantics).
  let floodZone: string | null = null;
  try {
    const fj = await backendFetch<Record<string, unknown>>(token, "/api/inbox/flood-check", {
      method: "POST",
      body: { address: args.address, id: args.id, org_id: args.orgId },
      ...common,
    });
    const zone = sv(fj?.["zone"]);
    if (zone) {
      floodZone = zone;
      if (sv(args.fields?.["flood"]) !== zone) edits["flood"] = zone;
      if (fj?.["stamped"] === true) stamped = true;
    }
  } catch { /* best-effort */ }

  return { edits, marketStatus, stamped, enrichedCount, floodZone, detail };
}
